import { useCallback, useState } from 'react';

import { getParametersByIds, getWorkingLists } from '../../../../services/catalogService';

/**
 * Loads the catalog's parameter lists (E7.4, #779) so a cohort node can take
 * one over as its criteria source. Lists are fetched once, lazily -- most
 * edit sessions never open the picker.
 */
export const useParameterListPicker = () => {
  const [parameterLists, setParameterLists] = useState([]);
  const [parameterListsLoading, setParameterListsLoading] = useState(false);
  const [parameterListsLoaded, setParameterListsLoaded] = useState(false);
  const [parameterListsError, setParameterListsError] = useState(false);
  const [listMembersById, setListMembersById] = useState({});
  const [listMembersErrorById, setListMembersErrorById] = useState({});
  const [expandingListId, setExpandingListId] = useState(null);

  const loadParameterLists = useCallback(async () => {
    if (parameterListsLoaded || parameterListsLoading) return;

    setParameterListsLoading(true);
    const result = await getWorkingLists();
    setParameterListsLoading(false);

    if (result.source === 'error') {
      // Nicht als "es gibt keine Listen" behandeln (B4, Review Runde 1): ein
      // API-Fehler ist ein anderer Zustand als eine leere Liste, und
      // `parameterListsLoaded` bleibt false, damit ein erneutes Oeffnen des
      // Menues einen neuen Versuch ausloest statt den Fehler einzufrieren.
      setParameterLists([]);
      setParameterListsError(true);
      return;
    }

    setParameterLists(result.lists);
    setParameterListsError(false);
    setParameterListsLoaded(true);
  }, [parameterListsLoaded, parameterListsLoading]);

  const resolveParameterListMembers = useCallback(async (list) => {
    const ids = Array.isArray(list?.parameterIds) ? list.parameterIds : [];
    if (ids.length === 0) return [];

    const result = await getParametersByIds(ids);
    return result.source === 'api' ? result.parameters : [];
  }, []);

  /**
   * Loest die einzelnen Elemente einer Liste auf, damit der Kriterien-Editor
   * eines davon (statt der ganzen Liste) einsetzen kann (AK1, #791). Ergebnis
   * je Liste zwischengespeichert -- ein erneutes Aufklappen derselben Liste
   * loest keinen zweiten Abruf aus. Ein leeres Ergebnis (Fehler oder
   * tatsaechlich leere Liste) wird nicht zwischengespeichert, damit ein
   * erneutes Aufklappen nach einem API-Fehler einen neuen Versuch macht
   * (dasselbe Muster wie B4 bei `loadParameterLists`).
   *
   * Review Runde 2, B5: ein API-Fehler wurde bisher genauso behandelt wie
   * eine tatsaechlich leere Liste (`resolveParameterListMembers` schluckt die
   * `source` und liefert in beiden Faellen `[]`). Das Ergebnis wird deshalb
   * hier direkt ueber `getParametersByIds` geholt, damit `source: 'error'`
   * je Liste festgehalten werden kann statt verloren zu gehen.
   */
  const loadParameterListMembers = useCallback(
    async (list) => {
      const id = list?.id;
      if (id === undefined || id === null) return;
      if ((listMembersById[id] || []).length > 0 || expandingListId === id) return;

      setExpandingListId(id);
      const ids = Array.isArray(list?.parameterIds) ? list.parameterIds : [];
      if (ids.length === 0) {
        setListMembersById((current) => ({ ...current, [id]: [] }));
        setListMembersErrorById((current) => ({ ...current, [id]: false }));
        setExpandingListId(null);
        return;
      }

      const result = await getParametersByIds(ids);
      setListMembersById((current) => ({ ...current, [id]: result.source === 'api' ? result.parameters : [] }));
      setListMembersErrorById((current) => ({ ...current, [id]: result.source === 'error' }));
      setExpandingListId(null);
    },
    [expandingListId, listMembersById],
  );

  return {
    parameterLists,
    parameterListsLoading,
    parameterListsError,
    loadParameterLists,
    resolveParameterListMembers,
    listMembersById,
    listMembersErrorById,
    expandingListId,
    loadParameterListMembers,
  };
};

export default useParameterListPicker;
